import TextEditor from '@ebg/vue-common/src/components/common/texteditor/TextEditor.vue';
import moment from 'moment/moment';
import { required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';

export default {
  name: 'AdvanceExpense',
  props: {
    record: {
      type: Object,
      default: null
    },
    showOnlyHead: {
      type: Boolean,
      default: false
    },
    hideHead: {
      type: Boolean,
      default: false
    },
    showPlainValues: {
      type: Boolean,
      default: false
    },
    values: {
      type: Object | Boolean,
      default: null
    },
    propCanEdit: {
      type: Boolean,
      default: true
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    recordData: {
      type: Object,
      default: null
    },
    recordFields: {
      type: Object | Boolean,
      default: null
    },
    params: {
      type: Object,
      default: null
    }
  },
  components: {
    TextEditor
  },
  data () {
    return {
      isLoading: false,
      banks: null,
      form: {
        register_date: moment().format('YYYY-MM-DD'),
        company_id: null,
        fullname: null,
        job_position_id: null,
        code: null,
        amount: null,
        comment: null,
        bank: null,
        bank_account: null,
        remember_bank_data: false,
        user_bank: null
      }
    };
  },
  methods: {
    loadBanks () {
      this.ApiRequest.request('user/banks', this.ApiRequest.REQUEST_GET, [], (response) => {
        if (response.data) {
          this.banks = response.data;
          if (!this.recordFields?.bank_account) {
            const primaryBank = this.banks.find(bank => bank.isPrimary);
            if (primaryBank) {
              this.form.user_bank = primaryBank.id;
              this.form.bank = primaryBank.bankName;
              this.form.bank_account = primaryBank.accountNumber;
            }
          }
        }
      });
    },
    loadExpenseCode () {
      this.ApiRequest.request('advance-forms/expense/code', this.ApiRequest.REQUEST_GET, [], (response) => {
        if (response.data) {
          this.form.code = response.data;
        }
      });
    },
    changeUserBank () {
      if (this.form.user_bank) {
        const bankData = this.banks.find((el) => el.id === this.form.user_bank);
        if (bankData) {
          this.form.bank = bankData.bankName;
          this.form.bank_account = bankData.accountNumber;
        }
      }
    },
    setJobPosition () {
      if (!this.form.job_position_id && this.jobPositionOptions[0]) {
        this.form.job_position_id = this.jobPositionOptions[0].value;
      }
    }
  },
  validations: {
    form: {
      job_position_id: { required },
      register_date: { required }
      /*
      amount: { required },
      purpose: { required },
      bank: { required },
      bank_account: { required }
       */
    }
  },
  computed: {
    amountComputed () {
      if (this.values?.amount > 0) {
        return this.values?.amount;
      }

      if (this.recordFields?.amount > 0) {
        return this.recordFields?.amount;
      }
    },
    commentComputed () {
      if (this.values?.comment) {
        return this.values?.comment;
      }

      if (this.recordFields?.comment) {
        return this.recordFields?.comment;
      }
    },
    bankComputed () {
      if (this.values?.bank) {
        return this.values?.bank;
      }

      if (this.recordFields?.bank) {
        return this.recordFields?.bank;
      }
    },
    bank_accountComputed () {
      if (this.values?.bank_account) {
        return this.values?.bank_account;
      }

      if (this.recordFields?.bank_account) {
        return this.recordFields?.bank_account;
      }
    },
    canEdit () {
      return !this.isLoading && this.propCanEdit; // @TODO
    },
    jobPositionOptions () {
      if (this.record && this.record.user && this.record.user.job_positions) {
        return this.HrHelper.getJobPositionOptions(this.record.user.job_positions);
      }
      if (this.recordData && this.recordData.job_positions) {
        return this.HrHelper.getJobPositionOptions(this.recordData.job_positions);
      }
      return [];
    },
    paymentPurposes () {
      return this.$store.getters.getConfigOptions('advance_expense_purposes', this.$i18n.locale);
    },
    paymentPurposeValue () {
      if (this.values) {
        return this.$store.getters.getParamFromConfig('advance_expense_purposes', this.values.purpose, 'name', this.$i18n.locale)
      }
    },
    companies () {
      return this.$store.getters.getConfigOptions('user_companies', this.$i18n.locale, 'disabled', '0');
    },
    registerDateFormatted () {
      if (this.values && this.values.register_date) {
        return moment(this.values.register_date).format('DD.MM.YYYY');
      }
    }
  },
  watch: {
    jobPositionOptions: {
      deep: true,
      immediate: true,
      handler (val) {
        // a reloaded list must not replace the position already chosen (or taken from the application)
        const keepsSelection = val?.some((option) => option.value === this.form.job_position_id);
        if (val?.length > 0 && !keepsSelection) {
          this.form.job_position_id = val[0].value;
        }
      }
    },
    form: {
      deep: true,
      immediate: true,
      handler (val) {
        val.isInvalid = (this.v$.$invalid === true);
        if (this.jobPositionOptions) {
          if (this.record && this.record.user) {
            val.job_positions = this.record.user.job_positions;
          }
        }
        this.$emit('input', val);
      }
    },
    'form.job_position_id': {
      deep: true,
      immediate: true,
      handler (val) {
        if (val && this.jobPositionOptions) {
          const option = this.jobPositionOptions.find((el) => el.value === val);
          if (option) {
            this.form.company_id = option.company_id;
          }
        }
      }
    },
    recordData: {
      deep: true,
      immediate: true,
      handler (val) {
        if (val) {
          this.form.code = val.code;
          // don't clobber a value the jobPositionOptions watcher already set correctly -
          // recordData doesn't reliably carry job_position_id itself, so an unconditional
          // assignment here was wiping out the real selection with undefined
          if (val.job_position_id) {
            this.form.job_position_id = val.job_position_id;
          }
          this.form.register_date = val.register_date;
          this.form.fullname = val.fullname;
        }
      }
    },
    record: {
      deep: true,
      immediate: true,
      handler (val) {
        if (val) {
          this.form.fullname = val.fullname;
        }
      }
    }
  },
  setup () {
    return { v$: useVuelidate() }
  },
  mounted () {
    if (!this.record?.id && !this.recordData) {
      this.loadExpenseCode();
      this.loadBanks();
      this.setJobPosition();
      this.$emit('input', Object.isInvalid = true);
    } else {
      if (this.record) {
        // merge, don't replace: record may not carry every field (e.g. job_position_id
        // lives on record.user.job_positions, not directly on record) - a plain
        // reassignment silently wiped those defaults, leaving required fields empty
        // even though the UI could still show stale selected text for them
        this.form = { ...this.form, ...this.record };
      }
      if (!this.form.job_position_id) {
        this.setJobPosition();
      }
      if (this.record && this.record.fields) {
        this.form.register_date = this.record.fields[0].register_date;
      } else {
        this.form.register_date = this.record?.register_date ?? this.recordData?.register_date;
      }
    }
    if (this.recordFields) {
      this.form.comment = this.recordFields.comment;
      this.form.amount = this.recordFields.amount;
      this.form.purpose = this.recordFields.purpose;
      this.form.user_bank = this.recordFields.user_bank;
      this.form.bank_account = this.recordFields.bank_account;
      this.form.bank = this.recordFields.bank;
    }
  }
}
